/**
 * 📱 SPACE SMS - Module Broadcast SMS
 * Centre de communication SMS en masse
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Radio, Send, Users, Filter, Loader2, RefreshCw,
  Wine, Leaf, Cigarette, Bell, AlertTriangle, CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../services/api';
import { toast } from 'sonner';

// Filtres de carte
const CARD_FILTERS = [
  { value: 'all', label: 'Tous les clients', icon: Users, color: 'cyan' },
  { value: 'alcohol', label: 'Carte Alcool', icon: Wine, color: 'red' },
  { value: 'cbd', label: 'Carte CBD', icon: Leaf, color: 'green' },
  { value: 'puffs', label: 'Carte Puffs', icon: Cigarette, color: 'purple' },
];

// Module SMS principal
const SpaceSMS = () => {
  const [smsMessage, setSmsMessage] = useState('');
  const [cardFilter, setCardFilter] = useState('all');
  const [previewCount, setPreviewCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(false);

  // Charger le nombre de destinataires
  const loadPreviewCount = async (filter) => {
    setIsLoadingCount(true);
    try {
      const count = await api.getSMSPreviewCount?.(filter) || { count: 0 };
      setPreviewCount(count.count || 0);
    } catch (error) {
      console.error('Erreur chargement compteur:', error);
      setPreviewCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  };

  useEffect(() => {
    loadPreviewCount(cardFilter);
  }, [cardFilter]);

  // Envoyer notification aux clients proches d'un cadeau
  const handleNotifyNearCompletion = async () => {
    try {
      const response = await api.notifyNearCompletion?.();
      if (response?.sent > 0) {
        toast.success(`🔔 ${response.sent} SMS envoyé(s) !`);
      } else {
        toast.info('Aucun client à notifier');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi SMS');
    }
  };

  // Envoyer SMS en masse
  const handleSendBroadcast = async () => {
    if (!smsMessage.trim()) {
      toast.error('Veuillez écrire un message');
      return;
    }

    const confirmSend = window.confirm(
      `Êtes-vous sûr de vouloir envoyer ce SMS à ${previewCount || 'tous les'} client(s) ?\n\nMessage: "${smsMessage}"`
    );

    if (!confirmSend) return;

    setIsSending(true);
    try {
      const response = await api.sendBulkSMS?.(cardFilter, smsMessage);
      toast.success(`📱 ${response?.sent || 0} SMS envoyé(s) !`);
      setSmsMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi SMS');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
            📱 BROADCAST
          </h1>
          <p className="text-gray-500 text-sm">Centre de communication SMS</p>
        </div>
      </div>

      {/* Notification rapide */}
      <div className="backdrop-blur-xl rounded-2xl p-5 border border-green-500/30 bg-green-500/5">
        <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
          <Bell className="w-5 h-5 text-green-400" />
          🔔 Notification Automatique
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          Envoie un SMS à tous les clients ayant 2 tampons ou moins restants sur une carte.
        </p>
        <Button
          onClick={handleNotifyNearCompletion}
          className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
        >
          <Bell className="w-4 h-4 mr-2" />
          Envoyer les notifications
        </Button>
      </div>

      {/* SMS Broadcast */}
      <div className="backdrop-blur-xl rounded-2xl p-5 border border-yellow-500/30 bg-[#12121a]/60">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Radio className="w-5 h-5 text-yellow-400" />
          📢 Envoi SMS en Masse
        </h3>

        {/* Sélection du filtre */}
        <div className="mb-6">
          <label className="text-sm text-gray-400 mb-3 block">🎯 Cibler les clients par carte</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CARD_FILTERS.map(({ value, label, icon: Icon, color }) => {
              const isActive = cardFilter === value;
              const colorClasses = {
                cyan: isActive ? 'border-cyan-500 bg-cyan-500/20' : 'border-gray-700 hover:border-cyan-500/50',
                red: isActive ? 'border-red-500 bg-red-500/20' : 'border-gray-700 hover:border-red-500/50',
                green: isActive ? 'border-green-500 bg-green-500/20' : 'border-gray-700 hover:border-green-500/50',
                purple: isActive ? 'border-purple-500 bg-purple-500/20' : 'border-gray-700 hover:border-purple-500/50',
              };

              return (
                <motion.button
                  key={value}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setCardFilter(value)}
                  className={`p-4 rounded-xl border-2 transition-all ${colorClasses[color]}`}
                >
                  <Icon className={`w-8 h-8 mx-auto mb-2 ${isActive ? `text-${color}-400` : 'text-gray-500'}`} />
                  <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-400'}`}>
                    {label}
                  </p>
                </motion.button>
              );
            })}
          </div>

          {/* Compteur de destinataires */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30"
          >
            <div className="flex items-center gap-2">
              {isLoadingCount ? (
                <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 text-cyan-400" />
              )}
              <span className="text-cyan-400 font-medium">
                📊 {previewCount} client(s) seront contactés
              </span>
            </div>
          </motion.div>
        </div>

        {/* Message */}
        <div className="mb-6">
          <label className="text-sm text-gray-400 mb-3 block">💬 Votre message</label>
          <textarea
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value)}
            placeholder="Ex: 🎉 Promo spéciale ce weekend ! -20% sur tous les spiritueux. La Petite Épicerie"
            rows={4}
            maxLength={160}
            className="w-full p-4 rounded-xl bg-[#1a1a25] border-2 border-yellow-500/30 text-white resize-none focus:border-yellow-500 focus:outline-none transition-colors"
          />
          <div className="flex justify-between mt-2">
            <span className="text-xs text-gray-500">
              💡 Gardez votre message court et percutant
            </span>
            <span className={`text-xs ${smsMessage.length > 140 ? 'text-yellow-400' : 'text-gray-500'}`}>
              {smsMessage.length}/160 caractères
            </span>
          </div>
        </div>

        {/* Bouton envoi */}
        <Button
          onClick={handleSendBroadcast}
          disabled={isSending || !smsMessage.trim() || previewCount === 0}
          className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 text-black font-bold h-12"
        >
          {isSending ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Envoi en cours...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Envoyer à {previewCount} client(s)
            </>
          )}
        </Button>

        {/* Avertissement */}
        <div className="mt-4 p-3 rounded-xl bg-orange-500/10 border border-orange-500/30">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5" />
            <p className="text-xs text-orange-400">
              Attention : Les SMS sont facturés. Utilisez cette fonctionnalité avec modération.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceSMS;
